import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, ChevronRight, Clock, ThumbsUp, Share2, CheckCircle2, Link as LinkIcon, Edit3, UserPlus, Sparkles } from "lucide-react";

export default function AnamneseTutorialPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-28 object-contain" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link 
              to="/" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Atendimento Clínico</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Como Digitalizar e Enviar Anamneses aos Pacientes
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>3 min de leitura / vídeo</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Atendimento Clínico
          </div>
        </div>

        {/* Video Player */}
        <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl mb-12 border-4 border-white bg-black">
          <iframe 
            width="100%" 
            height="100%" 
            src="https://www.youtube.com/embed/JvZMK2-KmRc" 
            title="Tutorial: Criando e Enviando Anamneses" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          ></iframe>
        </div>

        <div className="flex items-center justify-between border-b border-gray-200 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Instrutor" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
            <div>
              <p className="font-bold text-gray-900">Dr. Ricardo Assis</p>
              <p className="text-sm text-gray-500">Desenvolvimento de Produto</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <ThumbsUp size={18} /> Útil
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </div>

        {/* Written Tutorial */}
        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            Chega de fichas em papel na recepção. Com o módulo de anamnese do OdontoAssist AI, sua clínica digitaliza a coleta de informações dos pacientes com mais praticidade, organização e segurança.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>
          
          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                 Configuração dos Templates
               </h3>
               <p>Acesse o painel lateral em <strong>Anamnese &gt; Configurar Formulários</strong>. Você não precisa começar do zero! Carregue o nosso modelo padrão contendo perguntas básicas de triagem odontológica.</p>
               <p>Com o modelo carregado, você tem total liberdade: pode <strong>apagar</strong> questões indesejadas, tornar perguntas obrigatórias, e <strong>adicionar</strong> novas dependendo da sua especialidade (por exemplo, questões ortodônticas específicas).</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                 Envio de Link Público
               </h3>
               <div className="flex flex-col md:flex-row gap-6 mt-4">
                 <div className="flex-1">
                   <p>Após salvar seu template, retorne à lista principal de Anamneses. Clique no botão de <strong>Gerar Link Público</strong>.</p>
                   <p className="mt-2">O sistema irá gerar um endereço eletrônico. Copie este link e envie manualmente para o seu paciente através do seu <strong>WhatsApp, E-mail ou SMS</strong>. Ao abrir, o paciente verá uma página moderna, limpa e com a marca da sua clínica para responder pelo próprio celular.</p>
                 </div>
                 <div className="bg-indigo-50 p-6 rounded-2xl flex flex-col items-center justify-center gap-3 w-full md:w-48 shrink-0">
                   <LinkIcon className="text-indigo-600 mb-1" size={32} />
                   <span className="text-xs font-bold uppercase text-indigo-800 text-center">Fácil<br/>Compartilhamento</span>
                 </div>
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                 Recepção das Respostas Instantâneas
               </h3>
               <p>Assim que o paciente clica em finalizar o preenchimento no celular dele, as respostas <strong>já caem no seu sistema instantaneamente</strong>. Vá para a aba "Respostas Recebidas" para encontrar o nome do paciente aguardando.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                 Ações Rápidas (Alterar ou Cadastrar)
               </h3>
               <p>Ao abrir a anamnese preenchida, você consegue conduzir suas ações finais do atendimento:</p>
               <ul className="mt-2 list-none pl-0 space-y-4">
                  <li className="flex items-start gap-4">
                     <Edit3 className="shrink-0 text-indigo-500 mt-1" size={20} />
                     <div>
                       <strong>Alteração / Revisão:</strong><br/>
                       Reveja as informações enquanto o paciente estiver na cadeira e adicione qualquer anotação pertinente ou modifique algo que ele preencheu erroneamente.
                     </div>
                  </li>
                  <li className="flex items-start gap-4">
                     <UserPlus className="shrink-0 text-amber-500 mt-1" size={20} />
                     <div>
                       <strong>Transformar em Paciente:</strong><br/>
                       Se for um lead novo preenchendo a triagem pela primeira vez, utilize a função rápida no topo do formulário recebido para puxar todas as informações vitais e transformar essas fichas diretamente em um Cadastro Oficial de paciente no seu sistema de gestão!
                     </div>
                  </li>
               </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-purple-200 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-purple-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-black">5</span>
                 Revisão Contextual com IA
               </h3>
               <p>Depois de abrir uma anamnese vinculada ao paciente, use <strong>Revisar com IA</strong>. O sistema organiza as respostas, apresenta um resumo, destaca pontos para confirmação e sugere perguntas complementares.</p>
               <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-200 flex items-start gap-3">
                 <Sparkles className="text-purple-600 shrink-0 mt-1" size={20} />
                 <p className="text-purple-900 text-sm mb-0">A revisão não altera as respostas automaticamente. A profissional pode confirmar, editar ou descartar o resultado e deve conferir as informações com o paciente.</p>
               </div>
            </div>
          </div>

          <div className="bg-amber-100 rounded-3xl p-8 mt-12 text-amber-900 border border-amber-200">
             <h3 className="text-xl font-bold mb-4">Dica de Agilidade</h3>
             <ul className="space-y-3">
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-amber-600 shrink-0 mt-1" size={20} />
                 <span>Mantenha atalhos no seu celular ou computador com trechos de texto pré-configurados para enviar o link ainda mais rápido aos seus pacientes recorrentes.</span>
               </li>
             </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
